import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import "./App.css";
import { observer } from "mobx-react-lite";
import { globalVars } from "./components/global/GlobalVars.js";
import * as language from "./components/utils/language.js";
import * as navigation from "./components/utils/navigation.js";
import * as user from "./components/utils/user.js";
import * as resolvType from "./components/utils/resolveType.js";
import axios from "axios";
import Draggable from "react-draggable";

import Header from "./components/Header.jsx";
import HeaderMobile from "./components/HeaderMobile.jsx";
import Content from "./components/Content.jsx";
import ContentMobile from "./components/ContentMobile.jsx";
import Editor from "./components/Editor.jsx";
import HandleWebSocket from "./components/utils/Websocket.jsx";

import { configure } from "mobx";
configure({ enforceActions: "never" });

// ============================================================
// Hilfsfunktion: Erkennt ob Gerät mobil ist
// Kombiniert URL-Parameter /mobile mit automatischer Erkennung
// ============================================================
const detectMobile = (url) => {
  // 1. Expliziter URL-Parameter hat Vorrang
  if (url.toLowerCase().includes("mobile")) return true;
  if (url.toLowerCase().includes("desktop")) return false;

  // 2. Automatische Erkennung per Screen-Breite und Touch
  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isNarrowScreen = window.innerWidth <= 768;

  return isTouchDevice && isNarrowScreen;
};

const App = observer(() => {
  //========================================================================
  // Zustandsvariablen definieren
  const [init, setInit] = useState(false);
  const [visibilityHeader, setVisibilityHeader] = useState(true);
  const [cyclicUpdate, setCyclicUpdate] = useState(false);
  const [editorActive, setEditorActive] = useState(false);

  //========================================================================
  // url-Parameter auswerten
  //========================================================================
  /*
  /keyboard: Bildschirmtastatur einblenden
  /noheader: blendet den Header aus (z.B. für Einbetten in andere Website)
  /cyclicupdate: aktualisiert die Seite zyklisch (für Elemente platzieren auf der Seite)
  /editor: Editor einblenden
  /language: Sprachumschaltung einblenden
  /lang_xx: Sprache fixieren (lang_de -> deutsch, usw.), anstatt Browsersprache zu verwenden
  /startpage_pfad-zu-seite: Startseite pages/pfad/zu/seite.json
  /mobile: horizontale Visu für Smartphone
  */

  //========================================================================
  // url
  const url = window.location.href;

  //========================================================================
  // Mobile-Erkennung: URL-Parameter + Screen-Resize
  useEffect(() => {
    const updateMobile = () => {
      const isMobile = detectMobile(url);
      globalVars.setMobile(isMobile);
    };

    updateMobile();

    // Bei Orientierungswechsel oder Resize neu prüfen
    window.addEventListener("resize", updateMobile);
    window.addEventListener("orientationchange", updateMobile);

    return () => {
      window.removeEventListener("resize", updateMobile);
      window.removeEventListener("orientationchange", updateMobile);
    };
  }, [url]);

  //========================================================================
  // Header ausblenden (z.B. für Einbetten in andere Website)
  useEffect(() => {
    setVisibilityHeader(!url.toLowerCase().includes("noheader"));
  }, [url]);

  //========================================================================
  // Seite zyklisch aktualisieren (für Elemente platzieren auf der Seite)
  useEffect(() => {
    const active = url.toLowerCase().includes("cyclicupdate");
    setCyclicUpdate(active);
    globalVars.setCyclicUpdate(active);
  }, [url]);

  const oldTimeRef = useRef(null);

  useEffect(() => {
    if (cyclicUpdate && globalVars.dateTime.time !== oldTimeRef.current) {
      oldTimeRef.current = globalVars.dateTime.time;
      globalVars.setUpdateElementsTrigger(globalVars.dateTime.time);
    }
  }, [globalVars.dateTime.time, cyclicUpdate]);

  useEffect(() => {
    navigation.loadPageConfig(globalVars.pageProperties.actualPath, false);
  }, [globalVars.updateElementsTrigger]);

  //========================================================================
  // Editor aktivieren
  useEffect(() => {
    const active = url.toLowerCase().includes("editor");
    setEditorActive(active);
    globalVars.setEditor(active);
  }, [url]);

  //========================================================================
  // Sprache fixieren
  useEffect(() => {
    if (url.toLowerCase().includes("lang_de")) globalVars.setLanguage("de");
    else if (url.toLowerCase().includes("lang_it"))
      globalVars.setLanguage("it");
    else if (url.toLowerCase().includes("lang_en"))
      globalVars.setLanguage("en");
  }, [url]);

  //========================================================================
  // Startseite wählen
  useEffect(() => {
    const lowerUrl = url.toLowerCase();
    const key = "startpage_";

    if (lowerUrl.includes(key)) {
      let rest = url.substring(lowerUrl.indexOf(key) + key.length);
      rest = rest.split("/")[0];
      const page = rest.replaceAll("-", "/");
      globalVars.setStartpage(page);
    } else {
      if (!globalVars.mobile) {
        globalVars.setStartpage("startpage");
      } else {
        globalVars.setStartpage("startpageMobile");
      }
    }
  }, [url]);

  //========================================================================
  // Initialisierungen
  if (!init) {
    setInit(true);
    // Benutzerdaten laden
    user.checkIfLogin();
    if (globalVars.auth.loggedIn) {
      // Startseite initial laden
      const initStartpage = setInterval(() => {
        if (!globalVars.mobile) {
          navigation.changePage("home");
        } else {
          navigation.changePage("homeMobile");
        }
        if (!init) clearInterval(initStartpage);
      }, 500);
    } else {
      navigation.changePage("login");
      const initStartpage = setInterval(() => {
        navigation.changePage("login");
        if (!init) clearInterval(initStartpage);
      }, 500);
    }
    // Browsersprache ermitteln
    language.initLanguage();
  }

  //========================================================================
  // täglicher reload
  useEffect(() => {
    if (globalVars.dateTime.time === "03:42:00") {
      document.location.reload();
    }
  }, [globalVars.dateTime.time]);

  //========================================================================
  // Authentifizierung bei Benutzerinteraktion verlängern
  useEffect(() => {
    const eventListener = () => {
      if (globalVars.auth.loggedIn) {
        globalVars.auth.extendExpiration = new Date(
          new Date().getTime() + 5 * 60000,
        ).toISOString();
      }
    };

    document.addEventListener("touchstart", eventListener);
    document.addEventListener("click", eventListener);
    document.addEventListener("keydown", eventListener);

    return () => {
      document.removeEventListener("touchstart", eventListener);
      document.removeEventListener("click", eventListener);
      document.removeEventListener("keydown", eventListener);
    };
  }, []);

  // Authentifizierung zyklisch prüfen
  useEffect(() => {
    const checkAuth = setInterval(() => {
      const dateTimeAct = new Date().toISOString();
      if (globalVars.auth.loggedIn) {
        if (globalVars.auth.expiresAt) {
          if (dateTimeAct >= globalVars.auth.expiresAt) {
            if (dateTimeAct >= globalVars.auth.extendExpiration) {
              user.logout();
              navigation.changePage("login");
            }
          }
        } else {
          user.logout();
          navigation.changePage("login");
        }
      }
    }, 1000);

    return () => clearInterval(checkAuth);
  }, []);

  //========================================================================
  // Hintergrundbild laden
  const [imageSrc, setImageSrc] = useState(null);

  useEffect(() => {
    let isCancelled = false;
    let objectUrl = null;

    const fetchImage = async () => {
      try {
        const baseUrl =
          window.location.port === "3000"
            ? `http://${window.location.hostname}:${globalVars.httpPort}/project/images`
            : `/project/images`;

        let response;

        if (globalVars.pageProperties.currentPageConfig.showStartImage) {
          if (!globalVars.mobile) {
            response = await axios.get(`${baseUrl}/startbild.jpeg`, {
              responseType: "blob",
            });
          } else {
            response = await axios.get(`${baseUrl}/startbildMobil.jpeg`, {
              responseType: "blob",
            });
          }
        } else if (
          globalVars.pageProperties.currentPageConfig.backgroundImage !== ""
        ) {
          response = await axios.get(
            `${baseUrl}/${globalVars.pageProperties.currentPageConfig.backgroundImage}`,
            { responseType: "blob" },
          );
        }

        if (response && !isCancelled) {
          objectUrl = URL.createObjectURL(response.data);
          setImageSrc(objectUrl);
        }
      } catch (error) {
        // Bild nicht verfügbar
      }
    };

    if (
      globalVars.pageProperties.currentPageConfig.showStartImage ||
      (globalVars.pageProperties.currentPageConfig.backgroundImage &&
        globalVars.pageProperties.currentPageConfig.backgroundImage != "")
    ) {
      fetchImage();
    }
    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [
    globalVars.pageProperties.currentPageConfig.showStartImage,
    globalVars.pageProperties.currentPageConfig.backgroundImage,
    globalVars.mobile,
  ]);

  //========================================================================
  // Header-Höhe je nach Modus
  const headerHeight = globalVars.mobile ? 110 : 70;

  return (
    // ########################################################################################################
    // ################################################ Hintergrund ###########################################
    // ########################################################################################################
    <div
      className="page"
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        backgroundColor:
          // Kompatibilität: Standardvorlage hatte hellgrauen Hintergrund, diesen ignorieren
          globalVars.pageProperties.currentPageConfig.backgroundColor ===
          "#f0f0f0"
            ? "#878787"
            : globalVars.pageProperties.currentPageConfig.backgroundColor,
      }}
    >
      {imageSrc &&
        (globalVars.pageProperties.currentPageConfig.showStartImage ||
          (globalVars.pageProperties.currentPageConfig.backgroundImage &&
            globalVars.pageProperties.currentPageConfig.backgroundImage !=
              "")) && (
          <img
            style={{
              position: "absolute",
              top: visibilityHeader ? `${headerHeight}px` : "0px",
              left: "0px",
              width: window.innerWidth,
              height: visibilityHeader
                ? window.innerHeight - headerHeight
                : window.innerHeight,
            }}
            src={imageSrc}
          />
        )}
      {/* ######################################################################################################## */}
      {/* ################################################ Content ############################################### */}
      {/* ######################################################################################################## */}
      <div
        style={{
          position: "absolute",
          top: visibilityHeader ? `${headerHeight}px` : "0px",
        }}
      >
        {globalVars.mobile ? <ContentMobile /> : <Content />}
      </div>
      {/* ######################################################################################################## */}
      {/* ################################################ Header ################################################ */}
      {/* ######################################################################################################## */}
      {visibilityHeader && (globalVars.mobile ? <HeaderMobile /> : <Header />)}
      {/* ######################################################################################################## */}
      {/* ################################################ Editor ################################################ */}
      {/* ######################################################################################################## */}
      {editorActive && (
        <Draggable>
          <div
            style={{
              left: "100px",
              top: "100px",
              width: "500px",
              height: "100px",
            }}
          >
            <Editor />
          </div>
        </Draggable>
      )}
      {/* ######################################################################################################## */}
      {/* ################################################ Websocket-Verbindung ################################## */}
      {/* ######################################################################################################## */}
      <div
        style={{
          position: "absolute",
          top: "0px",
          left: "0px",
          width: "0px",
          height: "0px",
        }}
      >
        <HandleWebSocket />
      </div>
    </div>
  );
});

export default App;
